import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '../supabase';
import { useWhitelistRole } from '../useWhitelistRole';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, pageMobile, TAP_TARGET } from '../mobileStyles';

const COLOR_CHOICES = [
  '#6B1C2A', '#B83B3B', '#C97B32', '#C9A96E',
  '#4A7C59', '#2A5C3E', '#1C3E6B', '#6B2A5C',
  '#3E2A1A', '#7A8B8B', '#8B6914', '#4A1219',
];

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DOW_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MAX_DOTS = 4;

// Default palette applied (in order) to team members who don't have a
// color yet. When we encounter such a member we also backfill equipo.color
// in the DB so the assignment stays stable across reloads and tabs.
const JOURNAL_PALETTE = [
  '#6B1C2A', // wine
  '#C97B32', // gold
  '#2A5C3E', // green
  '#1C3E6B', // blue
  '#6B2A5C', // purple
  '#3E6B1C', // olive
];

// Deterministic 0..n hash so unknown authors (journal rows pointing at a
// name that's not in equipo) still get a stable color across renders.
function hashIndex(str, mod) {
  if (!str) return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % mod;
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Normalize whatever Supabase returns for `fecha` to a strict YYYY-MM-DD key.
// Guards against the column occasionally serializing as "YYYY-MM-DDTHH:mm:ssZ"
// (which would never match ymd(d) and could end up keyed on a different day).
function fechaKey(f) {
  if (!f) return '';
  return String(f).slice(0, 10);
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function initialsOf(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function firstNameOf(name) {
  return (name || '').trim().split(/\s+/)[0] || '';
}
function norm(s) {
  return (s == null ? '' : String(s)).trim().toLowerCase();
}
function emailLocal(e) {
  const n = norm(e);
  const at = n.indexOf('@');
  return at > 0 ? n.slice(0, at) : n;
}
// Build the set of "this autor belongs to this member" tokens. Includes the
// equipo nombre (full and first), the email (full and local-part) and any
// extra aliases passed in (e.g., the clerk display name). All normalized.
function memberAutorTokens(member, extras = []) {
  const tokens = new Set();
  const add = (v) => { const n = norm(v); if (n) tokens.add(n); };
  if (member) {
    add(member.nombre);
    add(firstNameOf(member.nombre));
    add(member.email);
    add(emailLocal(member.email));
  }
  for (const x of extras) {
    add(x);
    add(firstNameOf(x));
    add(emailLocal(x));
  }
  return tokens;
}
// True if a journal row's autor "belongs" to the given token set. Matches
// case/whitespace-insensitively and also catches "Brian Garcia" when the
// token set contains just "brian" (first-name substring on a word boundary).
function autorMatchesTokens(autor, tokens) {
  const a = norm(autor);
  if (!a || !tokens || tokens.size === 0) return false;
  if (tokens.has(a)) return true;
  const aFirst = a.split(/\s+/)[0];
  if (tokens.has(aFirst)) return true;
  for (const t of tokens) {
    if (!t) continue;
    if (a === t) return true;
    // word-boundary substring: token appears as its own word in autor
    const re = new RegExp(`(^|\\s)${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`);
    if (re.test(a)) return true;
  }
  return false;
}
function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-PE', { timeZone: 'America/Lima', hour: '2-digit', minute: '2-digit' });
}


function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h12" />
      <path d="M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2" />
      <path d="M6 6l1 10a2 2 0 002 2h2a2 2 0 002-2l1-10" />
      <line x1="9" y1="9" x2="9" y2="15" />
      <line x1="11" y1="9" x2="11" y2="15" />
    </svg>
  );
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const leadingBlanks = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function Journal() {
  const { user } = useUser();
  const { isAdmin, nombre: whitelistNombre } = useWhitelistRole();
  const { isMobile, isCompact } = useBreakpoint();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editorEntry, setEditorEntry] = useState(null);
  const [form, setForm] = useState({ autor: '', proyecto_id: '', tarea_id: '', nota: '' });
  // 'all' | 'diseño' | 'obra' — filters the task dropdown by the task's tipo.
  const [ganttFilter, setGanttFilter] = useState('all');
  // Tasks loaded on-demand for the currently selected project. We keep this
  // separate from the bulk `tasks` lookup so the dropdown can fall back to
  // the global list if the per-project fetch fails for any reason (e.g. RLS).
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectTasksLoading, setProjectTasksLoading] = useState(false);
  const projectTasksProjectIdRef = useRef(null);

  // Per-project task fetch — fires every time the user picks a different
  // project in the modal. Emits explicit logs so misalignments between
  // dropdown value (UUID) and tareas.proyecto_id (UUID) are easy to spot.
  useEffect(() => {
    const pid = form.proyecto_id || null;
    projectTasksProjectIdRef.current = pid;
    if (!pid) { setProjectTasks([]); return; }
    let cancelled = false;
    (async () => {
      setProjectTasksLoading(true);
      // eslint-disable-next-line no-console
      console.log('[journal] proyecto_id used:', pid);
      const { data, error } = await supabase
        .from('tareas')
        .select('*')
        .eq('proyecto_id', pid);
      if (cancelled || projectTasksProjectIdRef.current !== pid) return;
      // eslint-disable-next-line no-console
      console.log('[journal] tasks returned:', data);
      // eslint-disable-next-line no-console
      console.log('[journal] error:', error);
      setProjectTasks(Array.isArray(data) ? data : []);
      setProjectTasksLoading(false);
    })();
    return () => { cancelled = true; };
  }, [form.proyecto_id]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const colorPickerRef = useRef(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const canDeleteEntry = (entry) => !!entry && (isAdmin || entry.autor === myName);
  const [mode, setMode] = useState('mine'); // 'mine' | 'team'
  const [authorFilter, setAuthorFilter] = useState('all');

  const myName = useMemo(() => (
    whitelistNombre ||
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.primaryEmailAddress?.emailAddress ||
    ''
  ), [whitelistNombre, user]);

  const myEmail = (user?.primaryEmailAddress?.emailAddress || '').toLowerCase();

  const myMember = useMemo(() => {
    if (myEmail) {
      const byEmail = team.find(m => (m.email || '').toLowerCase() === myEmail);
      if (byEmail) return byEmail;
    }
    if (myName) {
      return team.find(m => m.nombre === myName) || null;
    }
    return null;
  }, [team, myEmail, myName]);

  async function changeMyColor(nextColor) {
    if (!nextColor) return;
    if (!myMember) {
      showToast('No hay un miembro de equipo asociado a tu cuenta', 'error');
      setColorPickerOpen(false);
      return;
    }
    if ((myMember.color || '').toLowerCase() === nextColor.toLowerCase()) {
      setColorPickerOpen(false);
      return;
    }
    setSavingColor(true);
    const { data, error } = await supabase
      .from('equipo')
      .update({ color: nextColor })
      .eq('id', myMember.id)
      .select();
    setSavingColor(false);
    if (error) {
      showToast('Error al guardar color: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se actualizó (posible bloqueo RLS en equipo)', 'error');
      return;
    }
    setTeam(prev => prev.map(m => m.id === myMember.id ? { ...m, color: nextColor } : m));
    setColorPickerOpen(false);
    showToast('Color actualizado');
  }

  // Close picker on outside click / Escape
  useEffect(() => {
    if (!colorPickerOpen) return;
    function onDoc(e) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setColorPickerOpen(false);
      }
    }
    function onKey(e) { if (e.key === 'Escape') setColorPickerOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [colorPickerOpen]);

  // The "Journal del equipo" tab is visible to everyone now, so no forced
  // fallback to 'mine' is needed.

  const fetchLookups = useCallback(async () => {
    const [tRes, pRes, taskRes] = await Promise.all([
      supabase.from('equipo').select('id, nombre, cargo, color, email').order('nombre'),
      supabase.from('proyectos').select('id, nombre').order('nombre'),
      supabase.from('tareas').select('id, nombre, proyecto_id, categoria, tipo').order('nombre', { nullsFirst: false }),
    ]);
    if (tRes.data) {
      const members = tRes.data;
      // Assign palette colors to members deterministically by their
      // position in the team, but skip any palette entry already taken
      // by an earlier member — guarantees no two members share a color
      // as long as the team size ≤ palette size. Backfill equipo.color
      // so the assignment is stable forever.
      const used = new Set();
      const withColors = members.map((m) => {
        if (m.color) { used.add(m.color); return m; }
        const pick = JOURNAL_PALETTE.find(c => !used.has(c)) || JOURNAL_PALETTE[0];
        used.add(pick);
        return { ...m, color: pick };
      });
      setTeam(withColors);
      members.forEach((m, i) => {
        if (m.color) return;
        supabase
          .from('equipo')
          .update({ color: withColors[i].color })
          .eq('id', m.id)
          .then(({ error }) => {
            if (error) console.warn('[journal] color backfill failed for', m.nombre, error.message);
          });
      });
    }
    if (pRes.data) setProjects(pRes.data);
    if (taskRes.data) setTasks(taskRes.data);
    if (taskRes.error) {
      // eslint-disable-next-line no-console
      console.warn('[journal] tareas fetch error', taskRes.error.message);
    } else {
      // eslint-disable-next-line no-console
      console.log('[journal] tareas loaded', (taskRes.data || []).length, 'rows');
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const from = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    const to = ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));
    const { data } = await supabase
      .from('journal')
      .select('*')
      .gte('fecha', from)
      .lte('fecha', to)
      .order('fecha', { ascending: true });
    setEntries(data || []);
    setLoading(false);
  }, [cursor]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Reset the one-shot debug log gate when the underlying inputs change so
  // the Mi Journal debug block re-runs after navigating months / re-login.
  useEffect(() => {
    if (typeof window !== 'undefined') window.__journal_mine_logged = false;
  }, [entries, team, myName, myEmail]);

  // Lookup: author name -> { color, initials } from equipo. Any team
  // member without color has already been backfilled with JOURNAL_PALETTE
  // by fetchLookups, so this map stays in sync with the DB.
  const authorMeta = useMemo(() => {
    // team is always pre-colorized by fetchLookups above — every member
    // has a unique color at this point.
    const map = {};
    for (const m of team) {
      map[m.nombre] = { color: m.color || JOURNAL_PALETTE[0], initials: initialsOf(m.nombre) };
    }
    return map;
  }, [team]);

  // Unknown authors (journal row whose autor doesn't match any equipo
  // nombre) still get a deterministic color via a small name-hash so
  // two different strangers don't both land on the same wine default.
  function metaFor(autor) {
    if (authorMeta[autor]) return authorMeta[autor];
    const color = JOURNAL_PALETTE[hashIndex(autor, JOURNAL_PALETTE.length)];
    return { color, initials: initialsOf(autor) };
  }

  const projectName = useCallback((id) => projects.find(p => p.id === id)?.nombre || null, [projects]);
  const taskName = useCallback((id) => tasks.find(t => t.id === id)?.nombre || null, [tasks]);

  const visibleEntries = useMemo(() => {
    let list = entries;
    if (mode === 'mine') {
      // Build a token set from every identity we know about the logged-in
      // user — whitelist nombre, clerk name, email, email local-part, equipo
      // row tied to their email — and match autor case/whitespace-insensitive
      // with a word-boundary fallback so "Brian Garcia" still matches "Brian".
      const tokens = memberAutorTokens(myMember, [myName, myEmail]);
      if (typeof window !== 'undefined' && !window.__journal_mine_logged) {
        const distinctAutores = Array.from(new Set(entries.map(e => e.autor))).sort();
        // eslint-disable-next-line no-console
        console.log('[journal] mi-journal debug', {
          myName,
          myEmail,
          myMemberNombre: myMember?.nombre || null,
          myMemberEmail: myMember?.email || null,
          tokens: Array.from(tokens),
          distinctAutoresInLoaded: distinctAutores,
          matched: entries.filter(e => autorMatchesTokens(e.autor, tokens)).map(e => e.autor),
        });
        window.__journal_mine_logged = true;
      }
      list = list.filter(e => autorMatchesTokens(e.autor, tokens));
    } else if (mode === 'team' && authorFilter !== 'all') {
      // The pill's value is the equipo member's nombre, but historical
      // journal rows for that person may use a different casing, just their
      // first name, or their email. Match through the same token-based logic.
      const filterMember = team.find(m => m.nombre === authorFilter) || null;
      const tokens = memberAutorTokens(filterMember, [authorFilter]);
      list = list.filter(e => autorMatchesTokens(e.autor, tokens));
    }
    return list;
  }, [entries, mode, authorFilter, myName, myEmail, myMember, team]);

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of visibleEntries) {
      const key = fechaKey(e.fecha);
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [visibleEntries]);

  // Per-day unique author dots (for team-view)
  const dotsByDay = useMemo(() => {
    const map = {};
    for (const [fecha, list] of Object.entries(entriesByDay)) {
      const seen = new Set();
      const dots = [];
      for (const e of list) {
        if (seen.has(e.autor)) continue;
        seen.add(e.autor);
        dots.push({ autor: e.autor, color: authorMeta[e.autor]?.color || JOURNAL_PALETTE[hashIndex(e.autor, JOURNAL_PALETTE.length)] });
        if (dots.length >= MAX_DOTS) break;
      }
      map[fecha] = dots;
    }
    return map;
  }, [entriesByDay, authorMeta]);

  const grid = useMemo(
    () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor]
  );

  const isTeamView = mode === 'team';

  function openDay(date) {
    setSelectedDate(date);
    // Always refresh the project/task lookups so a task that was just
    // deleted in Gantt doesn't linger as a selectable option here.
    fetchLookups();
    setGanttFilter('all');
    if (isTeamView) {
      // Read-only overview for team view
      setEditorEntry(null);
      setForm({ autor: '', proyecto_id: '', tarea_id: '', nota: '' });
    } else {
      setEditorEntry(null);
      setForm({ autor: myName, proyecto_id: '', tarea_id: '', nota: '' });
    }
  }

  function openEditorForEntry(entry) {
    setSelectedDate(new Date(entry.fecha + 'T00:00:00'));
    setEditorEntry(entry);
    fetchLookups();
    // Preselect the gantt selector from the saved value. Old notes without
    // gantt_tipo fall back to 'all' so the form stays editable without
    // forcing a tipo on legacy rows.
    setGanttFilter(entry.gantt_tipo === 'diseño' || entry.gantt_tipo === 'obra' ? entry.gantt_tipo : 'all');
    setForm({
      autor: entry.autor || myName,
      proyecto_id: entry.proyecto_id || '',
      tarea_id: entry.tarea_id || '',
      nota: entry.nota || '',
    });
  }

  function closeEditor() {
    setSelectedDate(null);
    setEditorEntry(null);
    setForm({ autor: '', proyecto_id: '', tarea_id: '', nota: '' });
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!selectedDate) return;
    setSaving(true);
    const payload = {
      fecha: ymd(selectedDate),
      autor: form.autor || myName || 'Usuario',
      proyecto_id: form.proyecto_id || null,
      tarea_id: form.tarea_id || null,
      nota: form.nota,
      gantt_tipo: ganttFilter === 'all' ? null : ganttFilter,
    };
    let result = editorEntry
      ? await supabase.from('journal').update(payload).eq('id', editorEntry.id)
      : await supabase.from('journal').insert([payload]);
    // Graceful fallback if the gantt_tipo column hasn't been migrated yet.
    if (result.error && /column .*gantt_tipo.* does not exist/i.test(result.error.message || '')) {
      const { gantt_tipo, ...rest } = payload;
      result = editorEntry
        ? await supabase.from('journal').update(rest).eq('id', editorEntry.id)
        : await supabase.from('journal').insert([rest]);
    }
    setSaving(false);
    if (result.error) {
      alert('Error al guardar: ' + result.error.message);
      return;
    }
    closeEditor();
    fetchEntries();
  }

  async function handleDelete() {
    if (!editorEntry) return;
    if (!window.confirm('¿Eliminar esta nota? No se puede deshacer.')) return;
    setSaving(true);
    const { data, error } = await supabase.from('journal').delete().eq('id', editorEntry.id).select();
    setSaving(false);
    if (error) { showToast('Error al eliminar: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se eliminó. Revisa la política DELETE en journal (RLS).', 'error');
      return;
    }
    closeEditor();
    fetchEntries();
    showToast('Nota eliminada');
  }

  // Delete from inside the "Nota del día" list — keep the day modal open so
  // the user can keep reviewing / deleting other notes for the same day.
  async function deleteEntryFromList(entry) {
    if (!entry || deletingId) return;
    if (!canDeleteEntry(entry)) return;
    if (!window.confirm('¿Eliminar esta nota? No se puede deshacer.')) return;
    setDeletingId(entry.id);
    const { data, error } = await supabase.from('journal').delete().eq('id', entry.id).select();
    setDeletingId(null);
    if (error) { showToast('Error al eliminar: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se eliminó. Revisa la política DELETE en journal (RLS).', 'error');
      return;
    }
    // Drop it from local state immediately so the list updates without
    // closing the modal; fetchEntries() syncs with DB in the background.
    setEntries(prev => prev.filter(e => e.id !== entry.id));
    fetchEntries();
    showToast('Nota eliminada');
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'proyecto_id') {
        // eslint-disable-next-line no-console
        console.log('[journal] project selected', value);
        // Drop the current task if it doesn't belong to the new project.
        if (next.tarea_id) {
          const t = tasks.find(t => t.id === next.tarea_id);
          if (!t || (t.proyecto_id && t.proyecto_id !== value)) next.tarea_id = '';
        }
      }
      return next;
    });
  }

  const availableTasks = useMemo(() => {
    // Prefer the per-project DB fetch when a project is chosen — that's the
    // authoritative source. Fall back to the global lookup if it returned
    // nothing (e.g. mid-load) so the dropdown isn't briefly empty.
    let list;
    if (form.proyecto_id) {
      const fromDirect = projectTasks.filter(t => t.proyecto_id === form.proyecto_id);
      list = fromDirect.length > 0
        ? fromDirect
        : tasks.filter(t => t.proyecto_id === form.proyecto_id);
    } else {
      list = tasks;
    }
    if (ganttFilter !== 'all') {
      list = list.filter(t => (t.tipo || 'obra') === ganttFilter);
    }
    if (form.proyecto_id) {
      // eslint-disable-next-line no-console
      console.log('[journal] available tasks for project', form.proyecto_id, 'filter', ganttFilter, '→', list.length);
    }
    return list;
  }, [tasks, projectTasks, form.proyecto_id, ganttFilter]);

  const tasksByCategoria = useMemo(() => {
    const groups = new Map(); // categoria → tasks[]
    for (const t of availableTasks) {
      const key = t.categoria || 'Sin categoría';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [availableTasks]);

  function taskLabel(t) {
    const base = t.nombre || '(sin nombre)';
    return t.categoria ? `${base} · ${t.categoria}` : base;
  }

  const selectedDayEntries = selectedDate ? entriesByDay[ymd(selectedDate)] || [] : [];

  // Team filter chips: "Todos" + every team member (derived from equipo table)
  const filterOptions = useMemo(() => {
    const opts = [{ value: 'all', label: 'Todos', color: null }];
    for (const m of team) {
      opts.push({ value: m.nombre, label: firstNameOf(m.nombre), color: m.color || '#6B1C2A' });
    }
    return opts;
  }, [team]);

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={s.header}>
        <div>
          <h2 style={isMobile ? { ...s.title, ...s.titleMobile } : s.title}>{isTeamView ? 'Journal del equipo' : 'Mi Journal'}</h2>
          <p style={isMobile ? { ...s.subtitle, ...s.subtitleMobile } : s.subtitle}>
            {isTeamView
              ? 'Registro de notas de todo el equipo — clic en un día para ver detalles'
              : 'Registro diario personal — clic en un día para añadir una nota'}
          </p>
          {!isTeamView && myName && (
            <div style={s.myColorRow} ref={colorPickerRef}>
              <button
                type="button"
                onClick={() => setColorPickerOpen(o => !o)}
                disabled={!myMember || savingColor}
                style={{
                  ...s.myColorDot,
                  background: myMember?.color || '#9a9a9a',
                  cursor: myMember ? 'pointer' : 'not-allowed',
                  opacity: myMember ? 1 : 0.6,
                  ...(isCompact ? {
                    ...s.myColorDotTouch,
                    background: `radial-gradient(circle, ${myMember?.color || '#9a9a9a'} 0 9px, #fff 9px 11px, rgba(0,0,0,0.15) 11px 12px, transparent 12.5px)`,
                  } : {}),
                }}
                title={myMember ? 'Cambiar mi color' : 'Tu cuenta no está vinculada al equipo'}
                aria-label="Cambiar mi color"
              />
              <span style={s.myColorName}>{myName}</span>
              {colorPickerOpen && myMember && (
                <div style={s.colorPickerPopover} role="menu">
                  <div style={s.colorPickerLabel}>Mi color en Journal</div>
                  <div style={s.colorPickerSwatchRow}>
                    {COLOR_CHOICES.map(c => {
                      const active = (myMember.color || '').toLowerCase() === c.toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => changeMyColor(c)}
                          disabled={savingColor}
                          aria-label={`Color ${c}`}
                          style={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: c,
                            border: active ? '2px solid #2D2D2D' : '2px solid #fff',
                            cursor: savingColor ? 'wait' : 'pointer',
                            padding: 0,
                            boxShadow: active ? '0 0 0 2px rgba(45,45,45,0.15)' : '0 1px 4px rgba(0,0,0,0.15)',
                            ...(isCompact ? s.colorSwatchTouch : {}),
                          }}
                        />
                      );
                    })}
                  </div>
                  <label style={s.colorPickerCustom}>
                    <input
                      type="color"
                      value={myMember.color || '#6B1C2A'}
                      onChange={(e) => changeMyColor(e.target.value)}
                      disabled={savingColor}
                      style={isCompact ? { width: TAP_TARGET, height: TAP_TARGET, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', cursor: savingColor ? 'wait' : 'pointer' } : { width: 28, height: 28, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', cursor: savingColor ? 'wait' : 'pointer' }}
                    />
                    <span>Personalizado</span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={isMobile ? { ...s.monthNav, ...s.monthNavMobile } : s.monthNav}>
          <button style={isCompact ? { ...s.navBtn, ...s.navBtnTouch } : s.navBtn} onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
          <span style={isMobile ? { ...s.monthLabel, ...s.monthLabelMobile } : s.monthLabel}>
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <button style={isCompact ? { ...s.navBtn, ...s.navBtnTouch } : s.navBtn} onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
          <button style={isCompact ? { ...s.todayBtn, ...s.todayBtnTouch } : s.todayBtn} onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}>Hoy</button>
        </div>
      </header>

      <div style={s.tabsRow}>
        <div style={isMobile ? { ...s.tabsWrap, ...s.tabsWrapMobile } : s.tabsWrap}>
          <button
            type="button"
            onClick={() => { setMode('mine'); setAuthorFilter('all'); }}
            style={isCompact ? { ...s.tab, ...s.tabTouch, ...(mode === 'mine' ? s.tabActive : {}) } : { ...s.tab, ...(mode === 'mine' ? s.tabActive : {}) }}
          >
            Mi Journal
          </button>
          <button
            type="button"
            onClick={() => setMode('team')}
            style={isCompact ? { ...s.tab, ...s.tabTouch, ...(mode === 'team' ? s.tabActive : {}) } : { ...s.tab, ...(mode === 'team' ? s.tabActive : {}) }}
          >
            Journal del equipo
          </button>
        </div>
          {isTeamView && (
            <div style={isMobile ? { ...s.filterBar, ...s.filterBarMobile } : s.filterBar}>
              {filterOptions.map(opt => {
                const active = authorFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAuthorFilter(opt.value)}
                    style={{
                      ...s.chip,
                      ...(active ? s.chipActive : {}),
                      ...(active && opt.color ? { borderColor: opt.color, background: `${opt.color}15`, color: opt.color } : {}),
                      ...(isCompact ? s.chipTouch : {}),
                    }}
                  >
                    {opt.color && <span style={{ ...s.chipDot, background: opt.color }} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
      </div>

      <div style={s.calendarCard}>
        <div style={s.dowRow}>
          {DOW_LABELS.map(l => <div key={l} style={isMobile ? { ...s.dowCell, ...s.dowCellMobile } : s.dowCell}>{l}</div>)}
        </div>
        <div style={s.grid}>
          {grid.map((d, idx) => {
            if (!d) return <div key={idx} style={isMobile ? { ...s.dayCell, ...s.dayCellMobile, ...s.dayBlank } : { ...s.dayCell, ...s.dayBlank }} />;
            const key = ymd(d);
            const dayEntries = entriesByDay[key] || [];
            const isToday = sameDay(d, today);
            const dayDots = dotsByDay[key];
            // Unique, ordered list of projects that appear on this day, with
            // a deterministic palette color per project id. Stacked vertically
            // in the cell, capped at 2 visible rows + "+N más".
            const projectItems = [];
            const seenProjectIds = new Set();
            for (const e of dayEntries) {
              const pid = e.proyecto_id;
              if (!pid || seenProjectIds.has(pid)) continue;
              const pname = projectName(pid);
              if (!pname) continue;
              seenProjectIds.add(pid);
              projectItems.push({
                id: pid,
                name: pname,
                color: JOURNAL_PALETTE[hashIndex(String(pid), JOURNAL_PALETTE.length)],
              });
            }
            const visibleProjects = projectItems.length > 3 ? projectItems.slice(0, 2) : projectItems;
            const hiddenProjectCount = projectItems.length - visibleProjects.length;
            return (
              <div
                key={idx}
                style={isMobile ? { ...s.dayCell, ...s.dayCellMobile, ...(isToday ? s.dayToday : {}) } : { ...s.dayCell, ...(isToday ? s.dayToday : {}) }}
                onClick={() => openDay(d)}
                role="button"
                tabIndex={0}
              >
                {isMobile ? (() => {
                  // Compact cell: day number + one dot per author (max 3) + count.
                  const authorColors = [];
                  const seenAuthors = new Set();
                  for (const e of dayEntries) {
                    const a = e.autor || '';
                    if (seenAuthors.has(a)) continue;
                    seenAuthors.add(a);
                    authorColors.push({ autor: a, color: metaFor(e.autor).color });
                  }
                  return (
                    <>
                      <div style={{ ...s.dayNumber, ...(isToday ? s.dayNumberToday : {}), ...s.dayNumberMobile }}>{d.getDate()}</div>
                      {dayEntries.length > 0 && (
                        <div style={s.dayDotsMobile}>
                          {authorColors.slice(0, 3).map(a => (
                            <span key={a.autor} style={{ ...s.dayDotMobile, background: a.color }} />
                          ))}
                        </div>
                      )}
                      {dayEntries.length > 1 && (
                        <div style={s.dayCountMobile}>{dayEntries.length}</div>
                      )}
                    </>
                  );
                })() : (<>
                <div style={s.dayTop}>
                  <div style={{ ...s.dayNumber, ...(isToday ? s.dayNumberToday : {}) }}>{d.getDate()}</div>
                  {isTeamView && dayDots && dayDots.length > 0 && (
                    <div style={s.dotsRow}>
                      {dayDots.map(dt => (
                        <span
                          key={dt.autor}
                          style={{ ...s.memberDot, background: dt.color }}
                          title={dt.autor}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {projectItems.length > 0 && (
                  <div style={s.dayProjectList}>
                    {visibleProjects.map(p => (
                      <div
                        key={p.id}
                        style={s.dayProjectRow}
                        title={p.name}
                      >
                        <span style={{ ...s.dayProjectDot, background: p.color }} />
                        <span style={s.dayProjectName}>{p.name}</span>
                      </div>
                    ))}
                    {hiddenProjectCount > 0 && (
                      <div
                        style={s.dayProjectMore}
                        title={projectItems.slice(visibleProjects.length).map(p => p.name).join(', ')}
                      >
                        +{hiddenProjectCount} más
                      </div>
                    )}
                  </div>
                )}
                <div style={s.notesArea}>
                  {isTeamView ? (
                    dayEntries.length > 0 && (
                      <div style={s.teamDayCount}>
                        {dayEntries.length} {dayEntries.length === 1 ? 'nota' : 'notas'}
                      </div>
                    )
                  ) : (
                    <>
                      {dayEntries.slice(0, 5).map(e => {
                        const meta = metaFor(e.autor);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            style={{ ...s.notePill, background: `${meta.color}14`, color: meta.color }}
                            onClick={(ev) => { ev.stopPropagation(); openEditorForEntry(e); }}
                            title={e.nota}
                          >
                            <span style={{ ...s.noteDot, background: meta.color }} />
                            <span style={s.noteAuthor}>{firstNameOf(e.autor) || '•'}</span>
                            {e.gantt_tipo === 'diseño' && (
                              <span style={s.noteTipoBadge} title="Gantt de Diseño">📐</span>
                            )}
                            {e.gantt_tipo === 'obra' && (
                              <span style={s.noteTipoBadge} title="Gantt de Obra">🏗️</span>
                            )}
                            <span style={s.noteText}>{e.nota?.slice(0, 60) || 'Sin texto'}</span>
                          </button>
                        );
                      })}
                      {dayEntries.length > 5 && (
                        <span style={s.moreCount}>+{dayEntries.length - 5} más</span>
                      )}
                    </>
                  )}
                </div>
                </>)}
              </div>
            );
          })}
        </div>
        {loading && <div style={s.loadingOverlay}>Cargando…</div>}
      </div>

      {/* Team-view read-only day modal */}
      {selectedDate && isTeamView && !editorEntry && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeEditor}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <div>
                <div style={s.modalEyebrow}>Notas del equipo</div>
                <h3 style={s.modalTitle}>
                  {selectedDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
              </div>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeEditor}>✕</button>
            </div>

            <div style={isMobile ? { ...s.teamList, ...s.sectionMobile } : s.teamList}>
              {selectedDayEntries.length === 0 ? (
                <div style={s.emptyDay}>Sin notas de este día para el filtro seleccionado.</div>
              ) : (
                selectedDayEntries.map(e => {
                  const meta = metaFor(e.autor);
                  const pname = projectName(e.proyecto_id);
                  const tname = taskName(e.tarea_id);
                  const time = formatTime(e.created_at);
                  return (
                    <article key={e.id} style={{ ...s.teamEntry, borderLeftColor: meta.color }}>
                      <header style={s.teamEntryHeader}>
                        <div style={{ ...s.teamAvatar, background: `${meta.color}22`, color: meta.color }}>
                          {meta.initials}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={s.teamAuthor}>{e.autor || 'Sin autor'}</div>
                          <div style={s.teamMetaRow}>
                            {time && <span style={s.teamTime}>{time}</span>}
                            {pname && <span style={s.teamTag} title="Proyecto">· {pname}</span>}
                            {tname && <span style={s.teamTag} title="Tarea">· {tname}</span>}
                          </div>
                        </div>
                        {canDeleteEntry(e) && (
                          <button
                            type="button"
                            onClick={() => deleteEntryFromList(e)}
                            disabled={deletingId === e.id}
                            style={isCompact ? { ...s.entryDeleteBtn, ...s.entryDeleteBtnTouch } : s.entryDeleteBtn}
                            title="Eliminar nota"
                            aria-label="Eliminar nota"
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </header>
                      <p style={isMobile ? { ...s.teamNote, ...s.bodyTextMobile } : s.teamNote}>{e.nota || 'Sin texto'}</p>
                    </article>
                  );
                })
              )}
            </div>
            <div style={isMobile ? { ...s.teamFooter, ...s.teamFooterMobile, ...sheet.footer } : s.teamFooter}>
              <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.btnMobile } : s.cancelBtn} onClick={closeEditor}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Editor modal (personal or admin editing an entry) */}
      {selectedDate && (!isTeamView || editorEntry) && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeEditor}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <div>
                <div style={s.modalEyebrow}>{editorEntry ? 'Editar nota' : 'Nueva nota'}</div>
                <h3 style={s.modalTitle}>
                  {selectedDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
              </div>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeEditor}>✕</button>
            </div>

            {!editorEntry && selectedDayEntries.length > 0 && (
              <div style={isMobile ? { ...s.existingBlock, ...s.sectionMobile } : s.existingBlock}>
                <div style={s.existingLabel}>Notas de este día</div>
                {selectedDayEntries.map(e => (
                  <div key={e.id} style={s.existingRow}>
                    <button
                      type="button"
                      style={s.existingItem}
                      onClick={() => openEditorForEntry(e)}
                    >
                      <span style={s.existingAuthor}>{e.autor}</span>
                      {e.gantt_tipo === 'diseño' && (
                        <span style={s.noteTipoBadge} title="Gantt de Diseño">📐 Diseño</span>
                      )}
                      {e.gantt_tipo === 'obra' && (
                        <span style={s.noteTipoBadge} title="Gantt de Obra">🏗️ Obra</span>
                      )}
                      <span style={isMobile ? { ...s.existingText, ...s.bodyTextMobile } : s.existingText}>{e.nota || 'Sin texto'}</span>
                    </button>
                    {canDeleteEntry(e) && (
                      <button
                        type="button"
                        onClick={() => deleteEntryFromList(e)}
                        disabled={deletingId === e.id}
                        style={isCompact ? { ...s.entryDeleteBtn, ...s.entryDeleteBtnTouch } : s.entryDeleteBtn}
                        title="Eliminar nota"
                        aria-label="Eliminar nota"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSave} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Autor</span>
                  <select style={s.input} name="autor" value={form.autor} onChange={handleChange} required>
                    <option value="">Selecciona autor…</option>
                    {team.map(m => (
                      <option key={m.id} value={m.nombre}>{m.nombre}{m.cargo ? ` · ${m.cargo}` : ''}</option>
                    ))}
                    {myName && !team.find(m => m.nombre === myName) && (
                      <option value={myName}>{myName} (yo)</option>
                    )}
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Proyecto</span>
                  <select style={s.input} name="proyecto_id" value={form.proyecto_id} onChange={handleChange}>
                    <option value="">— Sin proyecto —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </label>
              </div>
              {form.proyecto_id && (
                <div style={s.ganttTypeRow}>
                  {[
                    { v: 'all',    l: 'Todos' },
                    { v: 'diseño', l: '📐 Gantt de Diseño' },
                    { v: 'obra',   l: '🏗️ Gantt de Obra' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => {
                        setGanttFilter(opt.v);
                        // Drop a selected task that won't be visible under the new filter.
                        if (form.tarea_id) {
                          const t = tasks.find(x => x.id === form.tarea_id);
                          if (t && opt.v !== 'all' && (t.tipo || 'obra') !== opt.v) {
                            setForm(prev => ({ ...prev, tarea_id: '' }));
                          }
                        }
                      }}
                      style={{ ...s.ganttTypeChip, ...(ganttFilter === opt.v ? s.ganttTypeChipActive : {}) }}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              )}
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Tarea {form.proyecto_id ? `· ${availableTasks.length}` : ''}</span>
                <select style={s.input} name="tarea_id" value={form.tarea_id} onChange={handleChange}>
                  <option value="">— Sin tarea —</option>
                  {projectTasksLoading && form.proyecto_id && (
                    <option value="" disabled>(cargando tareas…)</option>
                  )}
                  {!projectTasksLoading && tasksByCategoria.length === 0 && form.proyecto_id && (
                    <option value="" disabled>(este proyecto no tiene tareas{ganttFilter !== 'all' ? ` de ${ganttFilter}` : ''})</option>
                  )}
                  {tasksByCategoria.map(([cat, list]) => (
                    <optgroup key={cat} label={cat}>
                      {list.map(t => (
                        <option key={t.id} value={t.id}>{taskLabel(t)}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Qué hiciste hoy</span>
                <textarea
                  style={{ ...s.input, minHeight: 120, resize: 'vertical', fontFamily: "'DM Sans', sans-serif" }}
                  name="nota"
                  value={form.nota}
                  onChange={handleChange}
                  placeholder="Avances, decisiones, bloqueos…"
                  required
                />
              </label>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                {editorEntry && (
                  <button type="button" style={isMobile ? { ...s.deleteBtn, ...s.btnMobile } : s.deleteBtn} onClick={handleDelete} disabled={saving}>
                    Eliminar
                  </button>
                )}
                <span style={{ flex: 1 }} />
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.btnMobile } : s.cancelBtn} onClick={closeEditor}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.submitBtn, ...s.btnMobile } : s.submitBtn} disabled={saving}>
                  {saving ? 'Guardando…' : editorEntry ? 'Guardar cambios' : 'Añadir nota'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color: '#fff', padding: '10px 24px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
          zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? toastMobile : {}),
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

const WINE = '#6B1C2A';
const s = {
  container: { padding: '32px 36px', maxWidth: 1640 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 16 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },
  myColorRow: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  myColorDot: {
    width: 18, height: 18, borderRadius: '50%',
    border: '2px solid #fff',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.12)',
    padding: 0,
    flexShrink: 0,
  },
  myColorName: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    fontFamily: "'DM Sans', sans-serif",
  },
  colorPickerPopover: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    minWidth: 240,
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
    padding: 14,
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  colorPickerLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-light)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  colorPickerSwatchRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 8,
  },
  colorPickerCustom: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  monthNav: { display: 'flex', alignItems: 'center', gap: 8 },
  navBtn: {
    width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, color: WINE, fontWeight: 600,
  },
  monthLabel: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: 'var(--wine)',
    minWidth: 190, textAlign: 'center', textTransform: 'capitalize',
  },
  todayBtn: {
    padding: '6px 14px', borderRadius: 8, border: `1px solid ${WINE}33`, background: `${WINE}0A`,
    color: WINE, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
  },

  tabsRow: { display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', marginBottom: 16 },
  tabsWrap: {
    display: 'inline-flex', padding: 4, background: '#fff', border: '1px solid var(--border)',
    borderRadius: 10, gap: 2,
  },
  tab: {
    padding: '8px 16px', borderRadius: 7, border: 'none', background: 'transparent',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-secondary)',
    fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
  },
  tabActive: { background: WINE, color: '#fff' },

  filterBar: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
    borderRadius: 20, border: '1px solid var(--border)', background: '#fff',
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
  },
  chipActive: { borderColor: WINE, background: `${WINE}12`, color: WINE },
  chipDot: { width: 8, height: 8, borderRadius: '50%' },

  calendarCard: {
    background: '#fff', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', position: 'relative',
  },
  dowRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#FAF5ED', borderBottom: '1px solid var(--border)' },
  dowCell: {
    padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left',
  },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' },
  dayCell: {
    minHeight: 170, padding: '10px 12px', borderRight: '1px solid #f3efe9', borderBottom: '1px solid #f3efe9',
    cursor: 'pointer', transition: 'background 0.15s', display: 'flex', flexDirection: 'column', gap: 5,
    overflow: 'hidden', minWidth: 0,
  },
  dayBlank: { background: '#FDFBF8', cursor: 'default' },
  dayToday: { background: 'rgba(107,28,42,0.04)' },
  dayTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  dayNumber: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' },
  dayNumberToday: {
    width: 24, height: 24, borderRadius: '50%', background: WINE, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
  },
  dotsRow: { display: 'flex', gap: 5, alignItems: 'center' },
  memberDot: {
    width: 12, height: 12, borderRadius: '50%', display: 'inline-block',
    boxShadow: '0 0 0 1.5px #fff',
  },
  teamDayCount: {
    fontSize: 10, fontWeight: 700, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 'auto',
  },
  notesArea: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 },
  dayProjectList: {
    display: 'flex', flexDirection: 'column', gap: 2,
    margin: '0 0 2px',
    minWidth: 0,
  },
  dayProjectRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    minWidth: 0,
  },
  dayProjectDot: {
    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
  },
  dayProjectName: {
    fontSize: 10, fontWeight: 700, color: WINE,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    fontFamily: "'DM Sans', sans-serif",
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    minWidth: 0,
  },
  dayProjectMore: {
    fontSize: 9.5, fontWeight: 600, color: 'var(--text-light)',
    fontFamily: "'DM Sans', sans-serif",
    paddingLeft: 13,
  },
  notePill: {
    display: 'flex', alignItems: 'center', gap: 5, width: '100%',
    padding: '3px 6px', borderRadius: 5, border: 'none',
    cursor: 'pointer', fontSize: 11, fontFamily: "'DM Sans', sans-serif", textAlign: 'left',
    overflow: 'hidden',
  },
  noteDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  noteAuthor: { fontWeight: 700, flexShrink: 0 },
  noteTipoBadge: {
    fontSize: 10, fontWeight: 700, color: WINE,
    background: 'rgba(107,28,42,0.08)',
    padding: '1px 6px', borderRadius: 10,
    flexShrink: 0, lineHeight: 1.3,
  },
  noteText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.85 },
  moreCount: { fontSize: 10, color: 'var(--text-light)', fontWeight: 600, paddingLeft: 6 },

  loadingOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, color: 'var(--text-light)',
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)',
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 28px 0', gap: 12 },
  modalEyebrow: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-light)', fontWeight: 700 },
  modalTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: 'var(--wine)', margin: '6px 0 0', textTransform: 'capitalize' },
  closeBtn: {
    width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
    background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)',
  },

  teamList: { padding: '20px 28px 8px', display: 'flex', flexDirection: 'column', gap: 12 },
  emptyDay: { padding: '20px 0', textAlign: 'center', color: 'var(--text-light)', fontSize: 13 },
  teamEntry: {
    border: '1px solid var(--border)', borderLeft: '3px solid',
    borderRadius: 10, padding: '12px 14px', background: '#FDFBF8',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  teamEntryHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  teamAvatar: {
    width: 32, height: 32, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  teamAuthor: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  teamMetaRow: { fontSize: 11, color: 'var(--text-light)', display: 'flex', flexWrap: 'wrap', gap: 4 },
  teamTime: { fontWeight: 600 },
  teamTag: {},
  teamNote: { margin: 0, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  teamFooter: { padding: '12px 28px 24px', display: 'flex', justifyContent: 'flex-end' },

  existingBlock: { padding: '16px 28px 0', display: 'flex', flexDirection: 'column', gap: 6 },
  existingLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-light)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  existingRow: { display: 'flex', alignItems: 'stretch', gap: 6 },
  existingItem: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', borderRadius: 8,
    background: '#FDFBF8', border: '1px solid var(--border)', textAlign: 'left',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  existingAuthor: { fontSize: 11, fontWeight: 700, color: WINE },
  existingText: { fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  entryDeleteBtn: {
    width: 30, height: 30, borderRadius: 8,
    border: '1px solid rgba(184,59,59,0.25)', background: 'rgba(184,59,59,0.06)',
    color: '#B83B3B', cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, alignSelf: 'center',
  },

  form: { padding: '20px 28px 28px', display: 'flex', flexDirection: 'column', gap: 14 },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  ganttTypeRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  ganttTypeChip: {
    padding: '6px 12px', borderRadius: 999,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  ganttTypeChipActive: {
    background: WINE, borderColor: WINE, color: '#fff',
  },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.02em' },
  input: {
    padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13,
    fontFamily: "'DM Sans', sans-serif", color: 'var(--text-primary)', background: '#FDFBF8',
    outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  modalActions: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", color: 'var(--text-secondary)',
  },
  submitBtn: {
    padding: '9px 24px', borderRadius: 8, border: 'none', background: WINE, color: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  deleteBtn: {
    padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(184,59,59,0.3)', background: '#fff',
    color: '#B83B3B', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },

  // Mobile-only overrides (merged when useBreakpoint().isMobile).
  modalHeaderMobile: { padding: '0 16px 12px', borderBottom: '1px solid var(--border)' },
  sectionMobile: { paddingLeft: 16, paddingRight: 16 },
  formMobile: { padding: '16px 16px 0' },
  fieldRowMobile: { gridTemplateColumns: 'minmax(0, 1fr)' },
  teamFooterMobile: { padding: '12px 16px', borderTop: '1px solid var(--border)' },
  modalActionsMobile: {
    flexWrap: 'wrap', marginTop: 0, marginLeft: -16, marginRight: -16,
    padding: '12px 16px', borderTop: '1px solid var(--border)',
  },
  btnMobile: { minHeight: 44 },

  // Phase 2 — page, header, calendar (mobile) and tap targets (compact).
  titleMobile: { fontSize: 26 },
  subtitleMobile: { fontSize: 15 },
  myColorDotTouch: {
    width: TAP_TARGET, height: TAP_TARGET, border: 'none', boxShadow: 'none', borderRadius: '50%',
  },
  colorSwatchTouch: { width: 40, height: 40 },
  monthNavMobile: { width: '100%' },
  navBtnTouch: { width: TAP_TARGET, height: TAP_TARGET, flexShrink: 0 },
  monthLabelMobile: { minWidth: 0, flex: 1, fontSize: 20 },
  todayBtnTouch: { minHeight: TAP_TARGET, flexShrink: 0 },
  tabsWrapMobile: { display: 'flex', width: '100%', boxSizing: 'border-box' },
  tabTouch: { minHeight: TAP_TARGET, flex: 1 },
  filterBarMobile: {
    flexWrap: 'nowrap', overflowX: 'auto', width: '100%', minWidth: 0,
    WebkitOverflowScrolling: 'touch', paddingBottom: 2,
  },
  chipTouch: { minHeight: TAP_TARGET, flexShrink: 0, whiteSpace: 'nowrap' },
  dowCellMobile: { padding: '8px 0', fontSize: 10, letterSpacing: 0, textAlign: 'center' },
  dayCellMobile: {
    minHeight: 52, padding: '6px 2px', gap: 3, alignItems: 'center',
  },
  dayNumberMobile: { fontSize: 14 },
  dayDotsMobile: { display: 'flex', gap: 3, justifyContent: 'center' },
  dayDotMobile: { width: 6, height: 6, borderRadius: '50%', display: 'inline-block' },
  dayCountMobile: { fontSize: 9, fontWeight: 700, color: 'var(--text-light)', lineHeight: 1 },
  entryDeleteBtnTouch: { width: TAP_TARGET, height: TAP_TARGET },
  bodyTextMobile: { fontSize: 15 },
};
